import { useState, useEffect, useCallback } from 'react';
import { AnimGradientText } from '@readdy/anim/header/gradient-text/react';

const slides = [
  {
    src: "https://anniversary-car-rental.com/carimges/imagecar7.jpg",
    alt: "ランボルギーニ カウンタック 25thアニバーサリー ",
  },
  {
    src: "https://anniversary-car-rental.com/carimges/imagecar4.png",
    alt: "ランボルギーニ カウンタック 25thアニバーサリー サイドビュー",
  },
  {
    src: "https://anniversary-car-rental.com/carimges/imagecar5.png",
    alt: "ランボルギーニ カウンタック 25thアニバーサリー ",
  },
  {
    src: "https://anniversary-car-rental.com/carimges/imagecar6.png",
    alt: "ランボルギーニ カウンタック 25thアニバーサリー ",
  },
  {
    src: "https://anniversary-car-rental.com/carimges/imagecar8.jpg",
    alt: "ランボルギーニ カウンタック 25thアニバーサリー ",
  },
];

export default function AboutGallerySection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goToSlide = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex(index);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  const goToPrev = useCallback(() => {
    const newIndex = currentIndex === 0 ? slides.length - 1 : currentIndex - 1;
    goToSlide(newIndex);
  }, [currentIndex, goToSlide]);

  const goToNext = useCallback(() => {
    const newIndex = currentIndex === slides.length - 1 ? 0 : currentIndex + 1;
    goToSlide(newIndex);
  }, [currentIndex, goToSlide]);

  useEffect(() => {
    const timer = setInterval(() => {
      goToNext();
    }, 5000);
    return () => clearInterval(timer);
  }, [goToNext]);

  return (
    <section id="about" className="relative py-24 md:py-32 px-6 bg-foreground-950 overflow-hidden">
      <div className="relative z-10 max-w-[1240px] mx-auto">
        {/* Section Label */}
        <div className="flex items-center justify-center gap-4 mb-14">
          <span className="divider-line text-accent-300"></span>
          <span className="text-[10px] tracking-[0.5em] uppercase text-accent-300">— About us —</span>
          <span className="divider-line text-accent-300"></span>
        </div>

        {/* Two-column layout */}
        <div className="flex flex-col lg:flex-row items-center lg:items-start gap-12 lg:gap-14">
          {/* Left: Text Content */}
          <div className="w-full lg:w-[42%] text-center">
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl leading-tight text-background-50">
              <AnimGradientText duration={2400}>
                Welcome
                <br />
                <span className="italic text-accent-300">to</span>
                <br />
                Anniversary Car <br/>Event Rental
              </AnimGradientText>
            </h2>

            <div className="mt-10 space-y-5 text-sm md:text-base leading-loose text-background-50/75">
              <p>
                商業施設の集客イベント、展示会、PV・映画撮影、<br />
                アパレルブランドのプロモーションなどに。
              </p>
              <p>
                1970〜80年代のボーイズホビー、スーパーカーブームの象徴である<br />
                カウンタックは、あらゆる世代の目を釘付けにし、イベントの格を跳ね上げます。
              </p>
            </div>

            {/* Decorative element */}
            <div className="hidden lg:block mt-10">
              <div className="inline-flex items-center gap-3 text-background-50/30">
                <span className="block w-8 h-px bg-accent-300/60"></span>
                <span className="text-[10px] tracking-[0.3em] uppercase">Lamborghini Countach</span>
                <span className="block w-8 h-px bg-accent-300/60"></span>
              </div>
            </div>
          </div>

          {/* Right: Slideshow */}
          <div className="w-full lg:w-[58%]">
            {/* Main Slide Container */}
            <div className="relative w-full aspect-[16/9] bg-foreground-900/50 rounded-sm overflow-hidden group">
              {/* Images */}
              <div className="relative w-full h-full">
                {slides.map((slide, index) => (
                  <div
                    key={index}
                    className={`absolute inset-0 transition-opacity duration-600 ease-in-out ${
                      index === currentIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
                    }`}
                  >
                    <img
                      src={slide.src}
                      alt={slide.alt}
                      className="w-full h-full object-cover"
                      loading={index === 0 ? 'eager' : 'lazy'}
                    />
                  </div>
                ))}
                {/* Gradient overlay at bottom */}
                <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-foreground-950/55 to-transparent z-20 pointer-events-none"></div>
              </div>

              {/* Slide Counter */}
              <div className="absolute top-4 right-4 z-30 bg-foreground-950/70 backdrop-blur-sm px-3 py-1.5 rounded-sm text-background-50 text-xs tracking-wider">
                <span className="font-heading text-base">{String(currentIndex + 1).padStart(2, '0')}</span>
                <span className="text-background-50/40 mx-1">/</span>
                <span className="text-background-50/40">{String(slides.length).padStart(2, '0')}</span>
              </div>

              {/* Prev Button */}
              <button
                onClick={goToPrev}
                className="absolute left-3 top-1/2 -translate-y-1/2 z-30 w-10 h-10 flex items-center justify-center bg-foreground-950/60 hover:bg-foreground-950/80 backdrop-blur-sm text-background-50 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100 hover:scale-110 cursor-pointer border border-background-50/10"
                aria-label="前の画像"
              >
                <i className="ri-arrow-left-s-line text-lg"></i>
              </button>

              {/* Next Button */}
              <button
                onClick={goToNext}
                className="absolute right-3 top-1/2 -translate-y-1/2 z-30 w-10 h-10 flex items-center justify-center bg-foreground-950/60 hover:bg-foreground-950/80 backdrop-blur-sm text-background-50 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100 hover:scale-110 cursor-pointer border border-background-50/10"
                aria-label="次の画像"
              >
                <i className="ri-arrow-right-s-line text-lg"></i>
              </button>
            </div>

            {/* Dot Indicators */}
            <div className="flex items-center justify-center gap-2.5 mt-5">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`transition-all duration-400 rounded-full cursor-pointer ${
                    index === currentIndex
                      ? 'w-6 h-1.5 bg-accent-300'
                      : 'w-1.5 h-1.5 bg-background-50/25 hover:bg-background-50/45'
                  }`}
                  aria-label={`画像 ${index + 1} に移動`}
                />
              ))}
            </div>

            {/* Thumbnail Strip */}
            <div className="mt-6 overflow-x-auto scrollbar-hide">
              <div className="flex gap-2.5 justify-center min-w-max px-2">
                {slides.map((slide, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`shrink-0 w-20 h-14 md:w-24 md:h-16 rounded-sm overflow-hidden transition-all duration-300 cursor-pointer border-2 ${
                      index === currentIndex
                        ? 'border-accent-300 opacity-100 scale-105'
                        : 'border-transparent opacity-50 hover:opacity-80'
                    }`}
                    aria-label={`画像 ${index + 1}`}
                  >
                    <img
                      src={slide.src}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}